import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { preloadRoute } from '@/hooks/useRoutePreload';
import storilyIcon from '@/assets/storily-icon.png';
import storilyIconDark from '@/assets/storily-icon-dark.png';
import storilyIconOffwhite from '@/assets/storily-icon-offwhite.png';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuLink,
} from "@/components/ui/navigation-menu";
import ResourcesMegaMenu from '@/components/sections/header/ResourcesMegaMenu';
import PlatformMegaMenu from '@/components/sections/header/PlatformMegaMenu';
import MobileNavigation from '@/components/sections/header/MobileNavigation';

const BANNER_STORAGE_KEY = 'promo_banner_dismissed';
const BANNER_HEIGHT = 42; // Approximate height of the promo banner in pixels

const EditorialHeader: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  // Initialize based on route to prevent hydration mismatch on SSG
  const [isOverDarkHero, setIsOverDarkHero] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.location.pathname.replace(/\/$/, '') === '/agency-owners';
    }
    return false;
  });

  useEffect(() => {
    // Check if banner is visible (not dismissed) AND we're on the homepage
    const checkBannerVisibility = () => {
      const isDismissed = localStorage.getItem(BANNER_STORAGE_KEY);
      const isHomepage = window.location.pathname === '/' || window.location.pathname === '';
      setIsBannerVisible(!isDismissed && isHomepage);
    };
    
    checkBannerVisibility();
    
    // Listen for banner dismissal
    const handleBannerDismissed = () => {
      setIsBannerVisible(false);
    };
    
    const handleScroll = () => {
      // Calculate scroll progress from 0 to 1 over the first 100px of scroll
      const progress = Math.min(window.scrollY / 100, 1);
      setScrollProgress(progress);
      
      // Check if we're on agency-owners page (has dark hero)
      if (window.location.pathname.replace(/\/$/, '') === '/agency-owners') {
        const heroHeight = window.innerHeight * 0.9; // 90vh hero
        const headerHeight = 80;
        const currentPosition = window.scrollY + headerHeight;
        
        setIsOverDarkHero(currentPosition < heroHeight);
      } else {
        setIsOverDarkHero(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('promoBannerDismissed', handleBannerDismissed);
    window.addEventListener('storage', checkBannerVisibility);
    handleScroll(); // Run on mount to set initial state
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('promoBannerDismissed', handleBannerDismissed);
      window.removeEventListener('storage', checkBannerVisibility);
    };
  }, []);

  // Use dark styling when in dark theme OR when over a dark hero section
  const isDarkSection = theme === 'dark' || isOverDarkHero;
  
  // Interpolate values based on scroll progress
  const isScrolled = scrollProgress > 0.1;
  const bannerOffset = isBannerVisible ? BANNER_HEIGHT : 0;
  const maxWidth = 100 - (scrollProgress * 40); // Goes from 100% to 60%
  const borderRadius = scrollProgress * 12; // Goes from 0 to 12px
  const topPosition = (20 - (scrollProgress * 8)) + bannerOffset; // Goes from 20px to 12px, plus banner offset
  const blur = scrollProgress * 12; // Goes from 0 to 12px
  const bgOpacity = scrollProgress * 0.8; // Goes from 0 to 0.8

  return (
    <header 
      className="fixed left-1/2 -translate-x-1/2 z-50"
      style={{ 
        top: `${topPosition}px`,
        backgroundColor: isDarkSection 
          ? `rgba(17, 17, 17, ${bgOpacity})` 
          : `rgba(244, 242, 241, ${bgOpacity})`,
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        border: `1px solid ${isDarkSection ? `rgba(255, 255, 255, ${scrollProgress * 0.1})` : `rgba(0, 0, 0, ${scrollProgress * 0.05})`}`,
        borderRadius: `${borderRadius}px`,
        width: `${maxWidth}%`,
        maxWidth: '960px',
        minWidth: 'fit-content',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div className="px-6 lg:px-8 py-2 flex items-center justify-between gap-8 relative">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-start overflow-hidden" style={{ width: '120px' }}>
          {/* Icon stays anchored on the left */}
          <img 
            src={isDarkSection ? storilyIconOffwhite : storilyIcon} 
            alt="Storily" 
            width={28}
            height={28}
            className="transition-all duration-300 flex-shrink-0"
            style={{ height: '28px', width: 'auto' }}
          />
          {/* Text fades out to the right */}
          <span 
            className="font-semibold text-xl tracking-tight transition-all duration-300"
            style={{ 
              color: isDarkSection ? '#FFFFFF' : '#111111',
              opacity: 1 - (scrollProgress * 2),
              transform: `translateX(${scrollProgress * 50}px)`,
              maxWidth: isScrolled ? 0 : '100px',
              marginLeft: isScrolled ? 0 : '4px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            Storily
          </span>
        </Link>

        {/* Navigation - Desktop */}
        <NavigationMenu className="hidden lg:flex items-center absolute left-1/2 -translate-x-1/2">
          <NavigationMenuList className="space-x-4">
            {/* Platform Mega Menu */}
            <NavigationMenuItem>
              <NavigationMenuTrigger 
                className="text-sm font-medium transition-colors bg-transparent hover:bg-transparent data-[state=open]:bg-transparent"
                style={{ color: isDarkSection ? '#AAAAAA' : '#555555' }}
              >
                Platform
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <PlatformMegaMenu />
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Pricing - Simple Menu Item */}
            <NavigationMenuItem>
              <NavigationMenuLink 
                href="/pricing"
                className="group inline-flex h-10 w-max items-center justify-center px-4 py-2 text-sm font-medium transition-colors bg-transparent hover:bg-transparent"
                style={{ color: isDarkSection ? '#AAAAAA' : '#555555' }}
                onMouseEnter={() => preloadRoute('/pricing')}
              >
                Pricing
              </NavigationMenuLink>
            </NavigationMenuItem>

            {/* Resources Mega Menu */}
            <NavigationMenuItem>
            <NavigationMenuTrigger 
                className="text-sm font-medium transition-colors bg-transparent hover:bg-transparent data-[state=open]:bg-transparent"
                style={{ color: isDarkSection ? '#AAAAAA' : '#555555' }}
              >
                Resources
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ResourcesMegaMenu />
              </NavigationMenuContent>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* CTA */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg transition-colors"
            style={{ 
              color: isDarkSection ? '#AAAAAA' : '#555555',
            }}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>

          {/* Mobile Navigation - mobile only */}
          <div className="block lg:hidden">
            <MobileNavigation isDarkSection={isDarkSection} />
          </div>

          <button 
            className="hidden sm:flex px-5 py-1.5 text-white text-sm font-medium hover:bg-[#222222] hover:scale-[1.02] hover:shadow-md active:scale-[0.98] transition-all duration-200 items-center gap-2"
            style={{ 
              backgroundColor: '#000000',
              borderRadius: '8px'
            }}
            onClick={() => window.open('https://app.storymarketing.group/', '_blank')}
          >
            Sign In
          </button>
        </div>
      </div>
    </header>
  );
};

export default EditorialHeader;
